import { useEffect, useState } from "react";
import { X, Plus, Trash2, Save } from "lucide-react";
import { Button } from "../../../../components/UI/Button";
import pipelineStageApi from "../../../../api/pipelineStageApi";

const DEFAULT_COLOR = "#6b7280";
const PRESET_COLORS = [
  "#3b82f6",
  "#22c55e",
  "#eab308",
  "#f97316",
  "#ef4444",
  "#a855f7",
  "#14b8a6",
  "#6b7280",
];

/**
 * Modal to manage pipeline stages for the current account.
 * - Add new stage (name + color)
 * - Edit name/color inline
 * - Delete (soft delete)
 * - Reorder via up/down buttons
 */
const PipelineStagesManager = ({
  isOpen,
  onClose,
  accountId,
  onChanged,
}) => {
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dirty, setDirty] = useState({}); // id -> true
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState(PRESET_COLORS[0]);

  const load = async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const res = await pipelineStageApi.getByAccountId(accountId);
      if (res?.status === 1) setStages(res.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setDirty({});
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, accountId]);

  const markDirty = (id) => setDirty((d) => ({ ...d, [id]: true }));

  const handleFieldChange = (id, field, value) => {
    setStages((list) =>
      list.map((s) =>
        (s._id || s.id) === id ? { ...s, [field]: value } : s,
      ),
    );
    markDirty(id);
  };

  const handleMove = (index, delta) => {
    const next = [...stages];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    // Rewrite ordinal_number for all (0..N) and mark dirty.
    next.forEach((s, i) => {
      s.ordinalNumber = i;
      markDirty(s._id || s.id);
    });
    setStages(next);
  };

  const handleSave = async (stage) => {
    const id = stage._id || stage.id;
    await pipelineStageApi.updateById(id, {
      name: stage.name,
      color: stage.color,
      ordinalNumber: stage.ordinalNumber,
      isActive: stage.isActive !== false,
    });
    setDirty((d) => {
      const next = { ...d };
      delete next[id];
      return next;
    });
    onChanged?.();
  };

  const handleDelete = async (stage) => {
    if (!window.confirm(`Xóa giai đoạn "${stage.name}"?`)) return;
    const id = stage._id || stage.id;
    await pipelineStageApi.softDeleteById(id);
    await load();
    onChanged?.();
  };

  const handleAdd = async () => {
    const name = newName.trim();
    if (!name || !accountId) return;
    const maxOrd = Math.max(
      -1,
      ...stages.map((s) => s.ordinalNumber ?? 0),
    );
    await pipelineStageApi.create({
      accountId,
      shopId: null,
      name,
      color: newColor,
      ordinalNumber: maxOrd + 1,
      isActive: true,
    });
    setNewName("");
    setNewColor(PRESET_COLORS[0]);
    await load();
    onChanged?.();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-[560px] max-w-full max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <h2 className="text-[14px] font-semibold">
            Quản lý giai đoạn bán hàng
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100"
            aria-label="Đóng"
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {loading ? (
            <div className="text-[13px] text-gray-500 text-center py-6">
              Đang tải...
            </div>
          ) : (
            <div className="space-y-1.5">
              {stages.map((s, i) => {
                const id = s._id || s.id;
                const isDirty = dirty[id];
                return (
                  <div
                    key={id}
                    className="flex items-center gap-2 p-2 border border-gray-200 rounded-md bg-white"
                  >
                    <input
                      type="color"
                      value={s.color || DEFAULT_COLOR}
                      onChange={(e) =>
                        handleFieldChange(id, "color", e.target.value)
                      }
                      className="w-7 h-7 rounded border border-gray-200 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={s.name || ""}
                      onChange={(e) =>
                        handleFieldChange(id, "name", e.target.value)
                      }
                      className="flex-1 text-[13px] border border-gray-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary/40"
                    />
                    <div className="flex flex-col gap-0.5">
                      <button
                        onClick={() => handleMove(i, -1)}
                        disabled={i === 0}
                        className="text-[10px] text-gray-400 hover:text-gray-700 disabled:opacity-30"
                      >
                        ▲
                      </button>
                      <button
                        onClick={() => handleMove(i, 1)}
                        disabled={i === stages.length - 1}
                        className="text-[10px] text-gray-400 hover:text-gray-700 disabled:opacity-30"
                      >
                        ▼
                      </button>
                    </div>
                    <button
                      onClick={() => handleSave(s)}
                      disabled={!isDirty}
                      className="p-1.5 rounded hover:bg-gray-100 text-primary disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Lưu"
                    >
                      <Save size={14} />
                    </button>
                    <button
                      onClick={() => handleDelete(s)}
                      className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-500"
                      title="Xóa"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                );
              })}
              {stages.length === 0 && (
                <div className="text-[13px] text-gray-500 text-center py-6">
                  Chưa có giai đoạn nào
                </div>
              )}
            </div>
          )}

          <div className="mt-4 p-3 border border-dashed border-gray-300 rounded-md">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-2">
              Thêm giai đoạn mới
            </div>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={newColor}
                onChange={(e) => setNewColor(e.target.value)}
                className="w-7 h-7 rounded border border-gray-200 cursor-pointer"
              />
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Tên giai đoạn..."
                className="flex-1 text-[13px] border border-gray-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary/40"
                onKeyDown={(e) => e.key === "Enter" && handleAdd()}
              />
              <Button
                onClick={handleAdd}
                disabled={!newName.trim()}
                size="sm"
                className="h-8"
              >
                <Plus size={14} className="mr-1" />
                Thêm
              </Button>
            </div>
            <div className="flex items-center gap-1 mt-2">
              {PRESET_COLORS.map((c) => (
                <button
                  key={c}
                  onClick={() => setNewColor(c)}
                  className="w-5 h-5 rounded-full border-2"
                  style={{
                    backgroundColor: c,
                    borderColor: newColor === c ? "#111827" : "transparent",
                  }}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="px-4 py-3 border-t border-gray-100 flex justify-end">
          <Button variant="outline" onClick={onClose} size="sm">
            Đóng
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PipelineStagesManager;
